/* eslint complexity: ["error", 7] */
// increased due to ASOS premier
import PropTypes from 'prop-types';
import { css, styled } from 'styled-components';

import {
    Heading,
    Paragraph,
    Box,
    List,
    ThumbsUpIcon,
    ShieldIcon,
    Text,
    ListItem,
    Flex,
    THEME_UTILITIES,
    useBrand,
    BRANDS,
    Label,
    Checkbox
} from '@cof/plastic-components';
import TermsAndConditions from './../Stage/TermsAndConditions';
import { withText } from '../../text/textStore';
import partners from '../../partners';
import { useFormContext } from 'react-hook-form';
import { FORM_SECTIONS } from '../../pages/QuickCheck/Components/FormIDs';
import { StyledLightCheckbox } from '../BooleanField/BooleanField';
import PremierBannerWithText from '../PremierBanner';
import { useFormData } from '../FormDataContext/FormDataContext';

const { useColor } = THEME_UTILITIES;

const IconStyling = ({ theme }: any) => css`
    height: 3.2rem;
    width: 3.2rem;
    ${theme.breakpoints.mq.mobileMax} {
        height: ${theme.space.md};
        width: ${theme.space.md};
    }
`;

const FutureSoftSearchPermissionCheckbox = ({ text, textVariant }: any) => {
    const { register } = useFormContext();
    const { isEdit, formData } = useFormData();
    const CheckboxComponent = textVariant === 'light' ? StyledLightCheckbox : Checkbox;
    const prePopValue = formData?.formValues?.[FORM_SECTIONS.Permissions.FutureSoftSearch] ?? true; // need to default this to true for if the quotation is invalid
    const defaultValue = isEdit ? prePopValue?.toString() === 'true' : true;

    return (
        <Label mt="xs">
            <Flex>
                <CheckboxComponent
                    // @ts-expect-error TS2783: 'name' is specified more than once, so this usage will be overwritten.
                    name={FORM_SECTIONS.Permissions.FutureSoftSearch}
                    id={FORM_SECTIONS.Permissions.FutureSoftSearch}
                    {...register(FORM_SECTIONS.Permissions.FutureSoftSearch)}
                    defaultChecked={defaultValue}
                    data-qa-id={`${FORM_SECTIONS.Permissions.FutureSoftSearch}-input`}
                />
                <Paragraph pb={0} ml={1} mt="-3px" data-qm-allow variant={textVariant}>
                    {text({ path: 'Checkbox' })}
                </Paragraph>
            </Flex>
        </Label>
    );
};

FutureSoftSearchPermissionCheckbox.propTypes = {
    text: PropTypes.func.isRequired,
    textVariant: PropTypes.string.isRequired
};

const HeaderListItem = styled(ListItem)`
    margin-top: '1.6rem';
    margin-bottom: 0;
    div > div:first-child > span {
        ${IconStyling}
    }
    div > div:last-child {
        margin: auto;
    }
`;

const StyledList = styled(List)`
    padding: 0;
`;

const StyledBorder = styled(Box)`
    ${({ theme }: any) => css`
        padding: 20px;
        border: 2px solid transparent;
        border-radius: 8px;
        display: inline-block;
        width: 100%;
        ${theme.breakpoints.mq.mobileMax} {
            border-radius: 4px;
        }
    `}
`;

const HeaderListComponent = ({ Icon, text }: any) => {
    const iconColors = useColor({
        // @ts-expect-error TS2561: Object literal may only specify known properties, but 'asos' does not exist in type 'ColorMapProps'.
        asos: 'ASOS.purple350',
        capitalOne: 'capitalOne.pink250',
        luma: 'luma.purple500',
        littlewoods: 'littlewoods.purple550',
        very: 'very.pink500'
    });
    return (
        <HeaderListItem Icon={() => <Icon color={iconColors} />} data-qa-id="white-header-item">
            {/* @ts-expect-error TS2322: Type '"500"' is not assignable to type 'ResponsiveValue<"regular" | "medium" | "semiBold", DefaultTh */}
            <Text fontWeight="500" data-qm-allow>
                {text}
            </Text>
        </HeaderListItem>
    );
};

const ReasonsToQuote = ({ Text: text, nextButton, showASOSPremierBanner = false }: any) => {
    const brand = useBrand();
    const backgroundColors = useColor({
        capitalOne: 'capitalOne.blue750',
        fallback: 'global.grey50'
    });

    const applySectionBackgroundColors = useColor({
        asos: '#F1E8FF',
        capitalOne: 'global.white',
        // @ts-expect-error TS2322: Type '"#EBDEEC"' is not assignable to type '"global.black" | "global.grey50" | "global.grey100" | "g
        littlewoods: '#EBDEEC',
        // @ts-expect-error TS2322: Type '"#E7E6EF"' is not assignable to type '"global.black" | "global.grey50" | "global.grey100" | "g
        luma: '#E7E6EF',
        // @ts-expect-error TS2322: Type '"#FCE6F2"' is not assignable to type '"global.black" | "global.grey50" | "global.grey100" | "g
        very: '#FCE6F2'
    });

    const isPromoTnCs = [partners.VERY, partners.LITTLEWOODS].includes(brand);

    const textVariant = brand === BRANDS.CAPITAL_ONE ? 'light' : 'dark';
    return (
        <Box backgroundColor={backgroundColors} data-qa-id="rtq-apply-section">
            <Paragraph variant={textVariant} pb="sm" data-qm-allow>
                {text({ path: 'Description1' })}
                {/* @ts-expect-error TS2322: Type '"600"' is not assignable to type 'ResponsiveValue<"regular" | "medium" | "semiBold", DefaultTh */}
                <Text fontWeight="600" size="inherit" color="inherit" data-qm-allow>
                    {text({ path: 'DescriptionBold' })}
                </Text>
                {text({ path: 'Description2' })}
            </Paragraph>
            <Paragraph variant={textVariant} pb="md" data-qm-allow>
                {text({ path: 'Description3' })}
            </Paragraph>
            <Heading
                id="apply-screen:keeping-in-touch:keep-checking-heading"
                // @ts-expect-error TS2322: Type 'false | "global.white"' is not assignable to type 'ResponsiveValue<AllowedColors, DefaultTheme
                color={brand === BRANDS.CAPITAL_ONE && 'global.white'}
                level="3"
                textAlign="left"
                data-qa-id="RTQApplyHeading"
                data-qm-allow
            >
                {text({ path: 'Heading' })}
            </Heading>

            <FutureSoftSearchPermissionCheckbox text={text} textVariant={textVariant} />

            {isPromoTnCs && (
                <TermsAndConditions
                    modalPath="OfferText"
                    tAndcPath="OfferTAndC"
                    path="MoneyBackStatement"
                    id="offer-terms-and-conditions"
                />
            )}

            <TermsAndConditions
                modalSize="fullSize"
                tAndcPath="TAndC"
                path="Statement"
                modalPath="Text"
                id="terms-and-conditions"
                variant={textVariant}
            />
            {showASOSPremierBanner && (
                <Box mt="md">
                    <PremierBannerWithText brand={brand} />
                </Box>
            )}
            <StyledBorder
                backgroundColor={applySectionBackgroundColors}
                justifyContent="center"
                mt="md"
                data-qa-id="rtq-border"
            >
                <Flex justifyContent={['left', null, 'center']}>
                    <StyledList>
                        <HeaderListComponent text={text({ path: 'Instant' })} Icon={ThumbsUpIcon} data-qm-allow />
                        <HeaderListComponent text={text({ path: 'Impact' })} Icon={ShieldIcon} data-qm-allow />
                    </StyledList>
                </Flex>
                <Flex justifyContent="center">
                    <Flex display="inline-flex" width={['100%', null, 'auto']} flexDirection="column">
                        {nextButton}
                    </Flex>
                </Flex>
            </StyledBorder>
        </Box>
    );
};

ReasonsToQuote.propTypes = {
    Text: PropTypes.func.isRequired,
    nextButton: PropTypes.node,
    showASOSPremierBanner: PropTypes.bool
};

HeaderListComponent.propTypes = {
    text: PropTypes.string.isRequired,
    Icon: PropTypes.elementType.isRequired
};

export default withText('ReasonsToQuote')(ReasonsToQuote);
